import { useEffect, useState, useCallback } from "react";
import BookButton from "@/components/BookButton";
import { useSEO, breadcrumbLd } from "@/lib/seo";

const PHOTOS = [
  { src: "/images/property/exterior-main.jpg", caption: "Westbridge Inn & Suites from US Highway 65", category: "Exterior" },
  { src: "/images/property/king-room-wide.jpg", caption: "King Suite — renovated, with seating area", category: "Rooms" },
  { src: "/images/property/king-room-front.jpg", caption: "King bed with reading lights", category: "Rooms" },
  { src: "/images/property/king-room-angle.jpg", caption: "Jacuzzi Suite — king bed", category: "Rooms" },
  { src: "/images/property/work-desk-room.jpg", caption: "Work desk & leather chair — King Suite", category: "Rooms" },
  { src: "/images/property/room-bedside-detail.jpg", caption: "Bedside detail — crisp linens", category: "Details" },
  { src: "/images/property/room-window-detail.jpg", caption: "Reading corner", category: "Details" },
  { src: "/images/property/led-mirror-bathroom.jpg", caption: "LED-lit bathroom mirror", category: "Bath" },
  { src: "/images/property/bathroom-vanity.jpg", caption: "Sparkling quartz vanity", category: "Bath" },
];

const CATEGORIES = ["All", "Rooms", "Bath", "Details", "Exterior"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const visible = filter === "All" ? PHOTOS : PHOTOS.filter((p) => p.category === filter);

  useSEO({
    title: "Photo Gallery — Rooms, Suites & Property",
    description:
      "Real photos of Westbridge Inn & Suites in Carrollton, MO — king suites, jacuzzi suite, three-bedroom family suite, LED-lit bathrooms and the property on US Highway 65.",
    path: "/gallery",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        name: "Westbridge Inn & Suites Photo Gallery",
        url: "https://www.westbridgecarrollton.com/gallery",
        image: PHOTOS.map((p) => `https://www.westbridgecarrollton.com${p.src}`),
      },
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Gallery", path: "/gallery" }]),
    ],
  });

  const move = useCallback((dir) => {
    setLightbox((i) => (i === null ? null : (i + dir + visible.length) % visible.length));
  }, [visible.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, move]);

  return (
    <div data-testid="gallery-page">
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Photo gallery</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              Every photo,<br /><span className="italic text-ember">the real thing.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              No stock photography, no wide-angle tricks. These are our actual rooms, our actual
              bathrooms, and the actual building you'll see from US-65 — photographed the way you'll
              find them.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap gap-2" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { setFilter(c); setLightbox(null); }}
              className={`font-mono-label px-4 py-2 transition-colors ${filter === c ? "bg-ink text-canvas" : "bg-sand text-ink-soft hover:bg-dune"}`}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((p, i) => (
            <button
              key={p.src}
              onClick={() => setLightbox(i)}
              className="group text-left fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
              aria-label={`View photo: ${p.caption}`}
              data-testid={`gallery-photo-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-dune">
                <img
                  src={p.src}
                  alt={`${p.caption} — Westbridge Inn & Suites, Carrollton MO`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                  loading="lazy"
                />
              </div>
              <p className="mt-3 font-mono-label text-ink-muted group-hover:text-ember transition-colors">{p.caption}</p>
            </button>
          ))}
        </div>
      </section>

      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button className="absolute top-6 right-6 text-canvas text-4xl leading-none w-12 h-12" aria-label="Close" data-testid="lightbox-close">×</button>
          <button
            onClick={(e) => { e.stopPropagation(); move(-1); }}
            className="absolute left-3 md:left-8 text-canvas text-5xl w-12 h-12 hover:text-ember"
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >‹</button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={visible[lightbox].src} alt={visible[lightbox].caption} className="w-full max-h-[78vh] object-contain" />
            <figcaption className="mt-4 text-center font-mono-label text-canvas/80">
              {visible[lightbox].caption} · {lightbox + 1} / {visible.length}
            </figcaption>
          </figure>
          <button
            onClick={(e) => { e.stopPropagation(); move(1); }}
            className="absolute right-3 md:right-8 text-canvas text-5xl w-12 h-12 hover:text-ember"
            aria-label="Next photo"
            data-testid="lightbox-next"
          >›</button>
        </div>
      )}

      <section className="bg-sand py-16 text-center" data-testid="gallery-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 className="font-display text-4xl md:text-5xl text-ink">Like what you see?</h2>
          <div className="mt-6 inline-flex">
            <BookButton label="Book Direct & Save" testid="gallery-book-btn" />
          </div>
        </div>
      </section>
    </div>
  );
}
